import { useDealer } from "src/hooks/useDealer";
import { useApiCall } from "@porchpass/ui";
import {
  postDealerPortalV1DealerIdApplicationsAppIdLenderLenderIdSubmit,
  postDealerPortalV1DealerIdApplicationsAppIdSubmitLenders,
  putDealerPortalV1DealerIdApplicationsAppIdPrimaryLenderLenderId,
  type DomainLender,
  type DomainLoanApplicationDetails,
  type DtoSubmitApplicationToLenderResult,
} from "@porchpass/api-client";

function rejectIfUndefined<T>(
  promise: Promise<T | undefined>,
  message: string,
): Promise<T> {
  return promise.then((value) => {
    if (value === undefined) {
      throw new Error(message);
    }
    return value;
  });
}

export const useLenderOperations = (
  application: DomainLoanApplicationDetails,
  refetchApplication: () => Promise<void>,
) => {
  const { dealer_id } = useDealer();

  const setPrimaryLenderApi = useApiCall(
    async (lenderId: string) => {
      const appId = application.application_id ?? "";
      if (!dealer_id || !appId) {
        throw new Error("Application not found");
      }

      await putDealerPortalV1DealerIdApplicationsAppIdPrimaryLenderLenderId(
        dealer_id,
        appId,
        lenderId,
      );
      await refetchApplication();
    },
    {
      toast: {
        success: "Primary lender updated",
        error: "Error updating primary lender",
        useServerErrorMessage: true,
      },
    },
  );

  const submitToLenderApi = useApiCall(
    async (lenderId: string) => {
      const appId = application.application_id ?? "";
      if (!dealer_id || !appId) {
        throw new Error("Application not found");
      }

      await postDealerPortalV1DealerIdApplicationsAppIdLenderLenderIdSubmit(
        dealer_id,
        appId,
        lenderId,
      );
      await refetchApplication();
    },
    {
      toast: {
        success: "Application submitted",
        error: "Application failed to submit",
        useServerErrorMessage: true,
      },
    },
  );

  const submitToAllLendersApi = useApiCall(
    async (lenderIds?: DomainLender[]) => {
      const appId = application.application_id ?? "";
      if (!dealer_id || !appId) {
        throw new Error("Application not found");
      }

      const response =
        await postDealerPortalV1DealerIdApplicationsAppIdSubmitLenders(
          dealer_id,
          appId,
          lenderIds ? { lender_ids: lenderIds } : {},
        );

      if (response.status !== 200) {
        throw new Error("Applications failed to submit");
      }

      const data = response.data;
      await refetchApplication();
      return data.results ?? [];
    },
    {
      toast: {
        error: "Applications failed to submit",
        useServerErrorMessage: true,
      },
    },
  );

  const setPrimaryLender = (lenderId: string) => {
    if (application.primary_lender === lenderId) return Promise.resolve();

    return rejectIfUndefined(
      setPrimaryLenderApi.execute(lenderId),
      "Error updating primary lender",
    );
  };

  const submitToLender = (lenderId: string) => {
    return rejectIfUndefined(
      submitToLenderApi.execute(lenderId),
      "Application failed to submit",
    );
  };

  const submitToAllLenders = (lenderIds?: DomainLender[]) => {
    if (!dealer_id || !application.application_id) {
      return Promise.resolve([] as DtoSubmitApplicationToLenderResult[]);
    }

    return rejectIfUndefined(
      submitToAllLendersApi.execute(lenderIds),
      "Applications failed to submit",
    );
  };

  return {
    setPrimaryLender,
    submitToLender,
    submitToAllLenders,
  };
};
